import { useEffect, useRef, useState } from "react";

const GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 1000,
};

const SAMPLE_WINDOW_MS = 12000;
const TARGET_ACCURACY_M = 80;
const COARSE_ACCEPTABLE_ACCURACY_M = 450;
const HARD_REJECT_ACCURACY_M = 1500;
const MIN_STRICT_ACCURACY_M = 35;
const STILL_JITTER_WINDOW_MS = 12000;

function toRadians(value) {
  return (value * Math.PI) / 180;
}

function distanceMeters(a, b) {
  const earthRadius = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return 2 * earthRadius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function normalizeAccuracy(value) {
  return Number.isFinite(value) && value > 0 ? value : Infinity;
}

function estimateQuality(accuracy) {
  if (!Number.isFinite(accuracy)) return "unknown";
  if (accuracy <= 10) return "high";
  if (accuracy <= 20) return "medium";
  if (accuracy <= 80) return "low";
  if (accuracy <= 250) return "coarse";
  return "very_coarse";
}

function buildSmoothedFix(samples) {
  if (!samples.length) return null;

  let weightedLat = 0;
  let weightedLng = 0;
  let totalWeight = 0;
  let bestAccuracy = Infinity;

  samples.forEach((sample) => {
    const boundedAccuracy = Math.max(3, sample.accuracy);
    const weight = 1 / (boundedAccuracy * boundedAccuracy);
    weightedLat += sample.lat * weight;
    weightedLng += sample.lng * weight;
    totalWeight += weight;
    bestAccuracy = Math.min(bestAccuracy, sample.accuracy);
  });

  if (!totalWeight) return null;

  const latestSample = samples[samples.length - 1];

  return {
    lat: weightedLat / totalWeight,
    lng: weightedLng / totalWeight,
    accuracy: Math.round(bestAccuracy),
    rawAccuracy: Math.round(latestSample.accuracy),
    sampleCount: samples.length,
    timestamp: latestSample.timestamp,
    quality: estimateQuality(bestAccuracy),
  };
}

export function useGPS() {
  const [gpsPosition, setGpsPosition] = useState(null);
  const samplesRef = useRef([]);
  const lastAcceptedRef = useRef(null);

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGpsPosition({
        errorCode: "UNSUPPORTED",
        errorMessage: "Bu cihaz/tarayici konum servisini desteklemiyor.",
      });
      return undefined;
    }

    const handlePosition = (position) => {
      const coords = position?.coords;
      const lat = Number(coords?.latitude);
      const lng = Number(coords?.longitude);
      const accuracy = normalizeAccuracy(coords?.accuracy);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      if (accuracy > HARD_REJECT_ACCURACY_M) {
        return;
      }

      const now = Date.now();
      const candidate = { lat, lng, accuracy, timestamp: now };
      const previous = lastAcceptedRef.current;

      if (previous) {
        const movedMeters = distanceMeters(previous, candidate);
        const jitterMeters = Math.max(3, Math.min(accuracy, 15));
        const withinStillWindow = now - previous.timestamp <= STILL_JITTER_WINDOW_MS;
        const lessAccurate = accuracy >= previous.accuracy * 1.15;

        if (withinStillWindow && movedMeters < jitterMeters && lessAccurate) {
          return;
        }
      }

      const dynamicAccuracyThreshold = previous
        ? Math.max(MIN_STRICT_ACCURACY_M, previous.accuracy * 2.2)
        : COARSE_ACCEPTABLE_ACCURACY_M;

      if (accuracy > dynamicAccuracyThreshold) {
        // Mevcut iyi bir konum varsa, anlik cok kotu olcumle geri dusmeyi engelle.
        const recentlyUpdated = now - previous.timestamp <= 25000;
        if (recentlyUpdated && previous.accuracy <= TARGET_ACCURACY_M) {
          return;
        }
      }

      lastAcceptedRef.current = candidate;

      const cutoff = now - SAMPLE_WINDOW_MS;
      const nextSamples = [...samplesRef.current, candidate].filter(
        (sample) => sample.timestamp >= cutoff
      );
      samplesRef.current = nextSamples;

      const smoothedFix = buildSmoothedFix(nextSamples);
      if (!smoothedFix) return;

      setGpsPosition({
        lat: smoothedFix.lat,
        lng: smoothedFix.lng,
        accuracy: smoothedFix.accuracy,
        rawAccuracy: smoothedFix.rawAccuracy,
        sampleCount: smoothedFix.sampleCount,
        quality: smoothedFix.quality,
        updatedAt: smoothedFix.timestamp,
        errorCode: null,
        errorMessage: null,
      });
    };

    const handleError = (error) => {
      console.error("GPS error:", error);
      const code = Number(error?.code);
      const message =
        code === 1
          ? "Konum izni kapali. Tarayici ayarlarindan izin verin."
          : code === 2
            ? "Konum bilgisi su an alinamiyor. Cihazin konum servisini kontrol edin."
            : code === 3
              ? "Konum alma zamani asildi. Acik alanda tekrar deneyin."
              : "Konum alinirken hata olustu.";

      setGpsPosition((prev) => {
        if (Number.isFinite(prev?.lat) && Number.isFinite(prev?.lng)) {
          return {
            ...prev,
            errorCode: `WARN_${code || "UNKNOWN"}`,
            errorMessage: message,
          };
        }

        return {
          lat: null,
          lng: null,
          accuracy: null,
          rawAccuracy: null,
          sampleCount: 0,
          quality: "unknown",
          updatedAt: Date.now(),
          errorCode: `ERR_${code || "UNKNOWN"}`,
          errorMessage: message,
        };
      });
    };

    navigator.geolocation.getCurrentPosition(handlePosition, handleError, GEO_OPTIONS);
    const watchId = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      GEO_OPTIONS
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  return gpsPosition;
}
